/**
 * This is the client-side entrypoint for your tRPC API. It is used to create the `api` object which
 * contains the Next.js App-wrapper, as well as your type-safe React Query hooks.
 *
 * We also create a few inference helpers for input and output types.
 */
import { httpBatchLink, httpLink, loggerLink, splitLink } from '@trpc/client';
import { createTRPCNext } from '@trpc/next';
import { type inferRouterInputs, type inferRouterOutputs } from '@trpc/server';
import { MutationCache } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { devalueTransformer } from '~/server/logic/transport/transformer';
import { getBillingGuardrailMessage } from '~/utils/billing-guardrails';

import { type AppRouter } from '~/server/api/root';

const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    return '';
  } // browser should use relative url
  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`;
  } // SSR should use vercel url
  return `http://localhost:${process.env.PORT ?? 3000}`; // dev SSR should use localhost
};

export let accessToken: string | undefined;

export function setAccessToken(newToken?: string) {
  accessToken = newToken;
}

/** A set of type-safe react-query hooks for your tRPC API. */
export const api = createTRPCNext<AppRouter>({
  config() {
    const linkOptions = {
      url: `${getBaseUrl()}/api/trpc`,
      headers: () => {
        return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
      },
      transformer: devalueTransformer,
    };

    return {
      queryClientConfig: {
        mutationCache: new MutationCache({
          onError: (error) => {
            const billingMessage = getBillingGuardrailMessage(error.message);
            if (billingMessage) {
              notifications.show({
                id: 'billing-limit-error',
                title: 'Action blocked by billing limits',
                message: billingMessage,
                color: 'red',
                autoClose: 10_000,
              });
            }
          },
        }),
      },
      /**
       * Links used to determine request flow from client to server.
       *
       * @see https://trpc.io/docs/links
       */
      links: [
        loggerLink({
          enabled: (opts) => {
            // This loggerLink only controls console logging in development.
            // Production error tracking (HyperDX, etc.) is unaffected.
            if (process.env.NODE_ENV !== 'development') {
              return false;
            }
            // Always log if TRPC_SHOW_LOGS is set
            if (process.env.TRPC_SHOW_LOGS) {
              return true;
            }
            // Log errors, but skip known long-running mutations that error expectedly
            // recorderWeb.start runs for the VM lifetime and errors on timeout (expected behavior)
            const skipConsoleErrorLogging = ['recorderWeb.start'];
            if (opts.direction === 'down' && opts.result instanceof Error) {
              const { path } = opts as unknown as { path?: string };
              return !path || !skipConsoleErrorLogging.includes(path);
            }
            return false;
          },
        }),
        splitLink({
          condition(op) {
            return op.context.skipBatch === true;
          },
          true: httpLink(linkOptions),
          false: httpBatchLink(linkOptions),
        }),
      ],
    };
  },
  /**
   * Whether tRPC should await queries when server rendering pages.
   *
   * @see https://trpc.io/docs/nextjs#ssr-boolean-default-false
   */
  ssr: false,
  transformer: devalueTransformer,
});

/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<AppRouter>;

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>;
